import { MaterialCommunityIcons } from '@expo/vector-icons';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spacing } from '../../styles';
import { Colors } from '../../themes';
import { SlideAction, ViewMode } from './enums';
import { SlideLayoutContextType, SlideLayoutProps } from './interfaces';

const SlideLayoutContext = createContext<SlideLayoutContextType | null>(null);

export const useSlideLayout = () => {
  const context = useContext(SlideLayoutContext);
  if (!context) {
    throw new Error('useSlideLayout must be used within a SlideLayout');
  }
  return context;
};

export const SlideLayout: React.FC<SlideLayoutProps> = ({
  leftColumn,
  rightColumn,
  viewMode = ViewMode.SINGLE,
  onViewModeChange,
}) => {
  const animationDuration = 500;
  const [searchParams] = useSearchParams();
  const direction = searchParams.get('direction');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(viewMode);
  const [sectionHeight, setSectionHeight] = useState<number>(600);
  const [columnTitles, setColumnTitles] = useState<{ left?: string; right?: string }>({});
  
  const HEADER_HEIGHT = 65;
  const LEFT_COLUMN_WIDTH_PERCENTAGE = 45;
  const RIGHT_COLUMN_WIDTH_PERCENTAGE = 55;

  const setContainerHeight = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    const headerHeight = (leftColumn.showHeader !== false) ? HEADER_HEIGHT : 0;
    setSectionHeight(height - headerHeight);
  }, [leftColumn.showHeader, HEADER_HEIGHT]);

  // Get current visible columns based on view mode and start index
  const visibleColumns = useMemo(() => {
    if (currentViewMode === ViewMode.SINGLE) {
      return {
        left: leftColumn,
        right: null
      };
    } else {
      return {
        left: leftColumn,
        right: rightColumn || null
      };
    }
  }, [leftColumn, rightColumn, currentViewMode]);
  
  // Animation values for smooth transitions
  const leftColumnTranslateX = useSharedValue(0);
  const rightColumnTranslateX = useSharedValue(0);
  const leftColumnOpacity = useSharedValue(1);
  const rightColumnOpacity = useSharedValue(1);
  const leftColumnWidth = useSharedValue(currentViewMode === ViewMode.SINGLE ? 100 : LEFT_COLUMN_WIDTH_PERCENTAGE);
  const rightColumnWidth = useSharedValue(currentViewMode === ViewMode.DUAL ? RIGHT_COLUMN_WIDTH_PERCENTAGE : 0);
  
  const switchToSingleMode = useCallback(() => {
    setCurrentViewMode(ViewMode.SINGLE);
    
    // Fade out right column and expand left
    rightColumnOpacity.value = withTiming(0, { duration: animationDuration });
    leftColumnWidth.value = withTiming(100, { duration: animationDuration });
    rightColumnWidth.value = withTiming(0, { duration: animationDuration });
    
    onViewModeChange?.(ViewMode.SINGLE);
  }, [onViewModeChange, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);

  const switchToDualMode = useCallback(() => {
    setCurrentViewMode(ViewMode.DUAL);
    leftColumnWidth.value = 100;
    rightColumnWidth.value = 0;
    
    // Expand right column and shrink left
    leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration });
    rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration });
    if (visibleColumns.right) {
      rightColumnOpacity.value = withTiming(1, { duration: animationDuration });
    }
    
    onViewModeChange?.(ViewMode.DUAL);
  }, [onViewModeChange, visibleColumns.right, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);
  
  const slideToNextColumn = useCallback(() => {
    // Fade out left column first
    leftColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
    
    setTimeout(() => {
      // Then shift right column to take left position
      rightColumnTranslateX.value = withTiming(-100, { duration: animationDuration / 2 });
      rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration / 2 });
    }, animationDuration / 2);
  }, [leftColumnOpacity, rightColumnTranslateX, rightColumnWidth]);

  const slideToPreviousColumn = useCallback(() => {
    // Fade out left column first
    rightColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
    
    setTimeout(() => {
      // Then shift right column to take left position
      leftColumnTranslateX.value = withTiming(80, { duration: animationDuration / 2 });
      leftColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration / 2 });
    }, animationDuration / 2);
  }, [rightColumnOpacity, leftColumnTranslateX, leftColumnWidth]);
  
  const fadeInRightColumns = useCallback(() => {
    if (visibleColumns.right) {
      // Start both columns invisible
      rightColumnOpacity.value = 0;
      rightColumnOpacity.value = withTiming(1, { duration: animationDuration / 2 });
    }
  }, [rightColumnOpacity, visibleColumns.right]);
  
  const executeSlideAction = useCallback((action?: SlideAction) => {
    switch (action) {
      case SlideAction.SINGLE:
        switchToSingleMode();
        break;
      case SlideAction.DUAL:
        switchToDualMode();
        break;
      case SlideAction.NEXT:
        slideToNextColumn();
        break;
      case SlideAction.PREV:
        slideToPreviousColumn();
        break;
      default:
        fadeInRightColumns();
        break;
    }
  }, [
    switchToSingleMode, 
    switchToDualMode, 
    slideToNextColumn, 
    slideToPreviousColumn, 
    fadeInRightColumns
  ]);

  // Handle direction from URL params on load
  useEffect(() => {
    const action = direction ? 
      direction.toLowerCase() as SlideAction :
      undefined;

    executeSlideAction(action);
  }, [direction, executeSlideAction]);


  const onSlideChange = useCallback((action: SlideAction): Promise<void> => {
    return new Promise((resolve) => {
      executeSlideAction(action);

      setTimeout(() => {
        resolve();
      }, animationDuration);
    });
  }, [executeSlideAction]);

  const handleBackPress = async (column: 'left' | 'right') => {
    const columnData = column === 'left' ? visibleColumns.left : visibleColumns.right;

    if (columnData?.backButtonTransition) {
      await onSlideChange(columnData?.backButtonTransition);
    } else {
      await onSlideChange(SlideAction.PREV);
    }
    
    if (columnData?.onBackButtonPress) {
      columnData.onBackButtonPress();
    } else {
      navigate(-1);
    }
  };

  const columnStyle = useMemo(() => {
    return {
      ...styles.sectionContent,
      height: sectionHeight
    };
  }, [sectionHeight]);

  const setColumnTitle = useCallback((column: 'left' | 'right', title: string | undefined) => {
    setColumnTitles(prev => ({ ...prev, [column]: title }));
  }, []);

  const leftColumnContextValue: SlideLayoutContextType = useMemo(() => ({
    column: 'left' as const,
    currentViewMode,
    setColumnTitle: (title: string | undefined) => setColumnTitle('left', title),
    onSlideChange,
  }), [currentViewMode, setColumnTitle, onSlideChange]);

  const rightColumnContextValue: SlideLayoutContextType = useMemo(() => ({
    column: 'right' as const,
    currentViewMode,
    setColumnTitle: (title: string | undefined) => setColumnTitle('right', title),
    onSlideChange,
  }), [currentViewMode, setColumnTitle, onSlideChange]);

  const leftColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${leftColumnTranslateX.value}%` }],
    opacity: leftColumnOpacity.value,
    width: `${leftColumnWidth.value}%`,
  }));

  const rightColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${rightColumnTranslateX.value}%` }],
    opacity: rightColumnOpacity.value,
    width: `${rightColumnWidth.value}%`,
  }));

  return (
    <View style={styles.container}>
      <View style={styles.columnsContainer}>
        {/* Left Column */}
        {visibleColumns.left && (
          <SlideLayoutContext.Provider value={leftColumnContextValue}>
            <Animated.View 
              style={[
                styles.leftColumn,
                leftColumnAnimatedStyle
              ]}
            >
              <View style={styles.columnContent} onLayout={setContainerHeight}>
                {visibleColumns.left.showHeader !== false && (
                  <View style={styles.header}>
                    <View style={styles.headerLeft}>
                      {visibleColumns.left.showBackButton && (
                        <Pressable
                          style={styles.backButton}
                          onPress={() => handleBackPress('left')}
                        >
                          <MaterialCommunityIcons
                            name="arrow-left"
                            size={24}
                            color={Colors.black}
                          />
                        </Pressable>
                      )}
                      <Text style={styles.headerTitleText}>
                        {t(columnTitles.left ?? visibleColumns.left.title)}
                      </Text>
                    </View>
                    {visibleColumns.left.headerComponent && (
                      <View style={styles.headerActions}>
                        {visibleColumns.left.headerComponent}
                      </View>
                    )}
                  </View>
                )}
                <View
                  style={columnStyle}
                >
                  {visibleColumns.left.disableScroll ? (
                    visibleColumns.left.mainComponent
                  ) : (
                    <ScrollView style={styles.scrollContainer}>
                      {visibleColumns.left.mainComponent}
                    </ScrollView>
                  )}
                </View>
              </View>
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}

          {/* Right Column */}
          {currentViewMode === ViewMode.DUAL && (
            <SlideLayoutContext.Provider value={rightColumnContextValue}>
              <Animated.View style={[
                rightColumnAnimatedStyle
              ]}>
                {visibleColumns.right && (
                  <View style={styles.columnContent}>
                    {visibleColumns.right.showHeader !== false && (
                      <View style={styles.header}>
                        <View style={styles.headerLeft}>
                          {visibleColumns.right.showBackButton && (
                            <Pressable
                              style={styles.backButton}
                              onPress={() => handleBackPress('right')}
                            >
                              <MaterialCommunityIcons
                                name="arrow-left"
                                size={24}
                                color={Colors.black}
                              />
                            </Pressable>
                          )}
                          <Text style={styles.headerTitleText}>
                            {t(columnTitles.right ?? visibleColumns.right.title)}
                          </Text>
                        </View>
                        {visibleColumns.right.headerComponent && (
                          <View style={styles.headerActions}>
                            {visibleColumns.right.headerComponent}
                          </View>
                        )}
                      </View>
                    )}
                  <View
                    style={columnStyle}
                  >
                    {visibleColumns.right.disableScroll ? (
                      visibleColumns.right.mainComponent
                    ) : (
                      <ScrollView style={styles.scrollContainer}>
                        {visibleColumns.right.mainComponent}
                      </ScrollView>
                    )}
                  </View>
                </View>
              )}
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  columnsContainer: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  leftColumn: {
    paddingRight: 16,
  },
  scrollContainer: {
    flex: 1,
  },
  columnContent: {
    flex: 1,
  },
  header: {
    borderRadius: 4,
    marginBottom: 4,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: Spacing.basePadding.base / 2,
    paddingHorizontal: Spacing.basePadding.base / 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitleText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.black,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginRight: 4,
  },
  sectionContent: {
    borderRadius: 4,
    backgroundColor: Colors.white,
    flexGrow: 1,
    overflow: 'hidden',
  },
});